<template>
  <div class="department-statistics">
    <!-- 顶部选项卡区域 -->
    <div class="header-section">
      <el-tabs v-model="activeMainTab" class="main-tabs">
        <el-tab-pane label="科室报表统计" name="table">
          <div class="tab-content"></div>
        </el-tab-pane>
        <el-tab-pane label="科室图表统计" name="chart">
          <div class="tab-content"></div>
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 表头条件筛选区域 -->
    <el-card class="filter-section">
      <div class="filter-container">
        <div class="filter-left">
          
          <div class="filter-item">
            <span class="filter-label">统计周期：</span>
            <el-date-picker
              v-model="filters.dateRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始日期时间"
              end-placeholder="结束日期时间"
              size="mini"
              style="width: 320px;"
              format="yyyy-MM-dd HH:mm:ss"
              value-format="yyyy-MM-dd HH:mm:ss"
            />
          </div>
          
          <div class="filter-right">
            <el-button type="primary" size="mini" icon="el-icon-Ssearch" @click="handleFilter">筛选</el-button>
            <el-button type="success" size="mini" icon="el-icon-download" @click="handleExport" :disabled="true">导出</el-button>
          </div>
        </div>
      </div>
      
      <div class="filter-desc">
        支持按统计时间段筛选、导出
      </div>
    </el-card>

    <!-- 中间内容区域：表格或图表 -->
    <div class="main-content-section">
      <!-- 表格区域 -->
      <el-card class="table-section" v-if="activeMainTab === 'table'">
        <!-- 加载状态 -->
        <div v-if="loading" class="loading-container">
          <el-skeleton :rows="5" animated />
        </div>
        
        <!-- 空状态 -->
        <div v-else-if="tableData.length === 0" class="empty-container">
          <el-empty description="暂无数据" />
        </div>
        
        <!-- 表格内容 -->
        <el-table
          v-else
          :data="currentTableData"
          border
          stripe
          style="width: 100%"
          size="mini"
          :max-height="tableHeight"
          :header-cell-style="{ background: '#f5f7fa', color: '#606266', 'text-align': 'center' }"
          :cell-style="{ 'text-align': 'center' }"
        >
          <el-table-column prop="departmentName" label="科室名称" width="120" fixed>
            <template slot-scope="{ row }">
              <div class="department-name">{{ row.departmentName }}</div>
            </template>
          </el-table-column>
          
          <el-table-column prop="outpatientVisits" label="门诊人次" width="100">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="统计周期内该科室接诊患者总数，体现科室业务规模" 
                placement="top"
              >
                <span>门诊人次</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ row.outpatientVisits.toLocaleString() }}
            </template>
          </el-table-column>
          
          <el-table-column prop="prescriptionCount" label="处方数量" width="100">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="周期内该科室医生开具的有效处方总数，反映诊疗活跃度" 
                placement="top"
              >
                <span>处方数量</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ row.prescriptionCount.toLocaleString() }}
            </template>
          </el-table-column>
          
          <el-table-column prop="totalAmount" label="科室总金额(元)" width="120">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="统计周期内该科室所有有效处方、诊疗项目的总收入" 
                placement="top"
              >
                <span>科室总金额(元)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.totalAmount) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="avgAmount" label="次均费用(元)" width="110">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="科室总金额 / 科室门诊人次，单患者平均消费，体现科室服务定价与消费结构" 
                placement="top"
              >
                <span>次均费用(元)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.avgAmount) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="mcAmount" label="药品处方金额(元)" width="120">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="该科室所有处方中药品收入的总额" 
                placement="top"
              >
                <span>药品金额(元)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.mcAmount) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="avgMcAmount" label="次均药费(元)" width="110">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="科室药品金额 / 科室门诊人次，单患者平均药品消费" 
                placement="top"
              >
                <span>次均药费(元)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.avgMcAmount) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="treatAmount" label="诊疗金额(元)" width="120">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="该科室所有处方中诊疗项目（含处置、检查等）的收入总额" 
                placement="top"
              >
                <span>诊疗金额(元)</span>
            </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.treatAmount) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="avgTreatAmount" label="次均诊疗费(元)" width="120">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="科室诊疗金额 / 科室门诊人次，单患者平均诊疗消费，反映科室技术服务价值" 
                placement="top"
              >
                <span>次均诊疗费(元)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.avgTreatAmount) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="purcAmt" label="药品成本(元)" width="120">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="该科室所有处方中开具药品的总成本（取药资成本数据，按科室汇总）" 
                placement="top"
              >
                <span>药品成本(元)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.purcAmt) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="eisaiPurcAmt" label="卫材成本(元)" width="120">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="该科室所有处方中处置项目带出的卫材总成本和卫材领用出库单中的成本金额（按科室汇总）" 
                placement="top"
              >
                <span>卫材成本(元)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.eisaiPurcAmt) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="treatCostAmt" label="项目成本(元)" width="120">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="该科室所有处方中开具的处置、检查等项目的总成本（按科室汇总）" 
                placement="top"
              >
                <span>项目成本(元)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.treatCostAmt) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="feeAmount" label="科室费用支出(元)" width="140">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="与该科室相关的一般费用单总支出" 
                placement="top"
              >
                <span>科室费用支出(元)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.feeAmount) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="deptProfit" label="科室毛利(元)" width="120">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="科室总金额 - 药品成本 - 卫材成本 - 项目成本 - 科室费用支出，反映科室真实盈利贡献" 
                placement="top"
              >
                <span>科室毛利(元)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatAmountWithSymbol(row.deptProfit) }}
            </template>
          </el-table-column>
          
          <el-table-column prop="deptProfitMargin" label="科室毛利率(%)" width="120">
            <template slot="header" slot-scope="scope">
              <el-tooltip 
                class="item" 
                effect="dark" 
                content="科室毛利 / 科室总金额 * 100%，直观体现科室盈利能力" 
                placement="top"
              >
                <span>科室毛利率(%)</span>
              </el-tooltip>
            </template>
            <template slot-scope="{ row }">
              {{ formatPercentage(row.deptProfitMargin) }}
            </template>
          </el-table-column>
          
          <!-- 操作列 -->
          <el-table-column label="操作" width="80" fixed="right">
            <template slot-scope="{ row }">
              <el-button
                type="text"
                size="mini"
                icon="el-icon-Nofullscreen"
                @click="handleDetail(row)"
              >
                详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页组件 -->
        <div class="pagination-section" v-if="tableData.length > 0">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.pageNum"
            :page-sizes="[10, 20, 30, 50]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            size="mini"
          >
          </el-pagination>
        </div>
      </el-card>

      <!-- 图表区域 - 修改为div标签 -->
      <div class="chart-section" v-if="activeMainTab === 'chart'">
        <div class="chart-controls">
          <el-select 
            v-model="mainChartType" 
            placeholder="选择图表类型" 
            size="mini" 
            style="width: 200px; margin-right: 10px;"
            @change="updateMainChart"
          >
            <el-option label="极坐标柱状图" value="polarBar"></el-option>
            <el-option label="折线图" value="line"></el-option>
            <el-option label="折线图（动态排序折线图）" value="lineSort"></el-option>
            <el-option label="饼图" value="pie"></el-option>
            <el-option label="柱状图" value="bar"></el-option>
            <el-option label="交错正负轴标签（柱状图）" value="barNegative"></el-option>
            <el-option label="断轴上的柱状图（柱状图）" value="barBreak"></el-option>
            <el-option label="可滚动的图例（饼图）" value="pieScroll"></el-option>
            <el-option label="相关矩阵（热力图，矩阵坐标系）" value="heatmap"></el-option>
            <el-option label="雷达图（自定义雷达图）" value="radar"></el-option>
            <el-option label="盒须图（数据聚合）" value="boxplot"></el-option>
            <el-option label="关系图（关系图自动隐藏重叠标签）" value="graph"></el-option>
            <el-option label="树图（折线树图）" value="tree"></el-option>
            <el-option label="矩形树图（矩形树图和旭日图的动画过渡）" value="treemap"></el-option>
            <el-option label="桑基图（桑基图左对齐布局）" value="sankey"></el-option>
            <el-option label="象形柱图（象形主图变形为柱状图）" value="pictorialBar"></el-option>
          </el-select>
          
          <el-select 
            v-model="mainChartField" 
            placeholder="选择统计字段" 
            size="mini" 
            style="width: 200px;"
            @change="updateMainChart"
          >
            <el-option 
              v-for="field in mainChartFields" 
              :key="field.key" 
              :label="field.label" 
              :value="field.key"
            ></el-option>
          </el-select>
        </div>
        
        <!-- 图表加载状态 -->
        <div v-if="loading" class="chart-loading">
          <el-skeleton :rows="3" animated />
        </div>
        
        <!-- 图表空状态 -->
        <div v-else-if="tableData.length === 0" class="chart-empty">
          <el-empty description="暂无数据，请先筛选获取数据" />
        </div>
        
        <!-- 图表容器 -->
        <div v-else class="chart-container">
          <!-- ECharts图表容器 -->
          <div ref="mainChartRef" style="width: 100%; height: 100%;"></div>
        </div>
      </div>
    </div>

    <!-- 说明区域 - 放在页面最底部 -->
    <el-card class="description-section">
      <div class="description-title">说明：</div>
      <div class="description-content">
        <p>1. 所有金额字段保留2位小数，毛利率保留2位小数；</p>
        <p>2. 筛选功能支持按科室名称、统计时间段精准筛选，可多科室对比查看；</p>
      </div>
    </el-card>

    <!-- 详情模态框 -->
    <el-dialog
      :title="`${detailData.departmentName} - 报表详细数据`"
      :visible.sync="detailDialogVisible"
      width="80%"
      top="5vh"
      custom-class="detail-dialog"
      :before-close="handleCloseDetail"
    >
      <div class="detail-container">
        <!-- 左侧区域：头像、科室名称、字段列表 -->
        <div class="detail-left">
          <!-- 科室头像和名称 -->
          <div class="dept-header">
            <div class="dept-avatar-section">
              <el-avatar
                :size="40"
                :src="detailData.deptImgUrl"
                v-if="detailData.deptImgUrl"
                class="dept-avatar"
              >
                {{ detailData.departmentName ? detailData.departmentName.charAt(0) : '' }}
              </el-avatar>
              <el-avatar
                :size="40"
                v-else
                class="dept-avatar"
              >
                {{ detailData.departmentName ? detailData.departmentName.charAt(0) : '' }}
              </el-avatar>
              
              <div class="dept-name-section">
                <span class="dept-name">{{ detailData.departmentName }}</span>
                <span class="online-indicator">
                  <i class="el-icon-success" style="color: #67c23a; margin-left: 5px;"></i>
                </span>
              </div>
            </div>
          </div>
          
          <!-- 分割线 -->
          <el-divider class="custom-divider"></el-divider>
          
          <!-- 字段列表 -->
          <div class="field-list-container">
            <div class="field-list-title">统计指标</div>
            <div class="field-list-content">
              <div 
                v-for="field in fields" 
                :key="field.key"
                class="field-item"
                :class="{ 'active': activeField === field.key }"
                @click="handleFieldClick(field.key)"
              >
                <span class="field-label">{{ field.label }}</span>
                <span class="field-count" v-if="getFieldCount(field.key) !== null">
                  ({{ getFieldCount(field.key) }})
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <!-- 右侧区域：详细内容展示 -->
        <div class="detail-right">
          <!-- 右侧选项卡：详细统计和图表统计 -->
          <el-tabs v-model="detailActiveTab" class="right-tabs">
            <!-- 详细统计选项卡 -->
            <el-tab-pane label="详细统计" name="detail">
              <div class="detail-statistics-content" v-if="activeField">
                <div class="statistics-title">
                  {{ getFieldLabel(activeField) }} - 详细数据
                </div>
                
                <!-- 使用Descriptions描述列表展示详细指标 -->
                <el-descriptions 
                  class="descriptions-container"
                  :column="2" 
                  border
                  size="small"
                >
                  <el-descriptions-item label="指标名称">
                    {{ getFieldLabel(activeField) }}
                  </el-descriptions-item>
                  
                  <el-descriptions-item label="指标值">
                    {{ getIndicatorValue(activeField) }}
                  </el-descriptions-item>
                  
                  <el-descriptions-item label="指标说明" :span="2">
                    {{ getFieldDescription(activeField) }}
                  </el-descriptions-item>
                  
                  <el-descriptions-item label="计算公式" :span="2" v-if="getFieldFormula(activeField)">
                    {{ getFieldFormula(activeField) }}
                  </el-descriptions-item>
                  
                  <el-descriptions-item label="数据时间" :span="2">
                    {{ getDataTimeRange() }}
                  </el-descriptions-item>
                  
                  <el-descriptions-item label="数据来源" :span="2">
                    系统自动统计
                  </el-descriptions-item>
                </el-descriptions>
                
                <!-- 其他相关指标卡片 -->
                <div class="related-indicators" v-if="getRelatedIndicators(activeField).length > 0">
                  <div class="related-title">相关指标</div>
                  <div class="indicators-grid">
                    <el-card 
                      v-for="item in getRelatedIndicators(activeField)" 
                      :key="item.key"
                      class="indicator-card"
                      shadow="hover"
                    >
                      <div class="card-header">
                        <div class="card-title">{{ item.label }}</div>
                        <div class="card-value">{{ getIndicatorValue(item.key) }}</div>
                      </div>
                    </el-card>
                  </div>
                </div>
              </div>
            </el-tab-pane>
            
            <!-- 图表统计选项卡 -->
            <el-tab-pane label="图表统计" name="chart">
              <div class="chart-statistics-content">
                <!-- 图表控制区域 -->
                <div class="chart-controls">
                  <div class="chart-type-selector">
                    <span class="selector-label">图表类型：</span>
                    <el-select 
                      v-model="chartType" 
                      placeholder="选择图表类型" 
                      size="mini" 
                      style="width: 150px; margin-right: 10px;"
                      @change="updateChart"
                    >
                      <el-option label="极坐标柱状图" value="polarBar"></el-option>
                      <el-option label="折线图" value="line"></el-option>
                      <el-option label="饼图" value="pie"></el-option>
                      <el-option label="柱状图" value="bar"></el-option>
                      <el-option label="散点图" value="scatter"></el-option>
                    </el-select>
                  </div>
                </div>
                
                <!-- 图表容器 -->
                <div class="chart-container">
                  <div ref="chartRef" style="width: 100%; height: 450px;"></div>
                </div>
                
                <!-- 图表说明 -->
                <div class="chart-description">
                  <div class="description-title">图表说明</div>
                  <div class="description-content">
                    展示当前科室各项指标的对比分析，可通过下拉框切换不同的图表类型查看数据
                  </div>
                </div>
              </div>
            </el-tab-pane>
          </el-tabs>
        </div>
      </div>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false" size="mini">关 闭</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { findAttribute } from "@/api/user.js"
import { otpatDept } from "@/api/statisticalReport/Report";
import * as echarts from 'echarts'

export default {
  name: 'DepartmentStatistics',
  data() {
	// 计算默认时间范围：结束时间为当前时间，开始时间为向前一周
	const endTime = new Date();
	const startTime = new Date();
	startTime.setDate(endTime.getDate() - 7);
    return {
      filters: {
		department: [],
		dateRange: [
		  this.formatDateTime(startTime, 'yyyy-MM-dd HH:mm:ss'),
		  this.formatDateTime(endTime, 'yyyy-MM-dd HH:mm:ss')
		]
	  },
      deptOptions: [],
      tableData: [], // 初始化为空数组
      loading: false, // 添加加载状态
      tableHeight: 400,
      pagination: {
        pageNum: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      detailActiveTab: 'detail',
      detailData: {},
      userInfo: {},
      searchKeyword: '',
      activeField: 'outpatientVisits',
      chartType: 'polarBar',
      chartInstance: null,
      // 在详情字段定义中，确保字段key与接口返回字段一致
      fields: [
        { 
          key: 'outpatientVisits', 
          label: '门诊人次',
          description: '统计周期内该科室接诊患者总数，体现科室业务规模',
          formula: null
        },
        { 
          key: 'prescriptionCount', 
          label: '处方数量',
          description: '周期内该科室医生开具的有效处方总数，反映诊疗活跃度',
          formula: null
        },
        { 
          key: 'totalAmount', 
          label: '科室总金额(元)',
          description: '统计周期内该科室所有有效处方、诊疗项目的总收入',
          formula: null
        },
        { 
          key: 'avgAmount', 
          label: '次均费用(元)',
          description: '单患者平均消费，体现科室服务定价与消费结构',
          formula: '科室总金额 / 科室门诊人次'
        },
        { 
          key: 'mcAmount', 
          label: '药品金额(元)',
          description: '该科室所有处方中药品收入的总额',
          formula: null
        },
        { 
          key: 'avgMcAmount', 
          label: '次均药费(元)',
          description: '单患者平均药品消费',
          formula: '科室药品金额 / 科室门诊人次'
        },
        { 
          key: 'treatAmount', 
          label: '诊疗金额(元)',
          description: '该科室所有处方中诊疗项目（含处置、检查等）的收入总额',
          formula: null
        },
        { 
          key: 'avgTreatAmount', 
          label: '次均诊疗费(元)',
          description: '单患者平均诊疗消费，反映科室技术服务价值',
          formula: '科室诊疗金额 / 科室门诊人次'
        },
        { 
          key: 'purcAmt', 
          label: '药品成本(元)',
          description: '该科室所有处方中开具药品的总成本（取药资成本数据，按科室汇总）',
          formula: null
        },
        { 
          key: 'eisaiPurcAmt', 
          label: '卫材成本(元)',
          description: '该科室所有处方中处置项目带出的卫材总成本和卫材领用出库单中的成本金额（按科室汇总）',
          formula: null
        },
        { 
          key: 'treatCostAmt', 
          label: '项目成本(元)',
          description: '该科室所有处方中开具的处置、检查等项目的总成本（按科室汇总）',
          formula: null
        },
        { 
          key: 'feeAmount', 
          label: '科室费用支出(元)',
          description: '与该科室相关的一般费用单总支出',
          formula: null
        },
        { 
          key: 'deptProfit', 
          label: '科室毛利(元)',
          description: '科室总金额 - 药品成本 - 卫材成本 - 项目成本 - 科室费用支出，反映科室真实盈利贡献',
          formula: '科室总金额 - 药品成本 - 卫材成本 - 项目成本 - 科室费用支出'
        },
        { 
          key: 'deptProfitMargin', 
          label: '科室毛利率(%)',
          description: '直观体现科室盈利能力',
          formula: '科室毛利 / 科室总金额 * 100%'
        }
      ],
      // 相关指标映射
      relatedIndicators: {
        outpatientVisits: ['avgAmount', 'avgMcAmount', 'avgTreatAmount'],
        prescriptionCount: ['totalAmount', 'mcAmount', 'treatAmount'],
        totalAmount: ['deptProfit', 'deptProfitMargin'],
        mcAmount: ['purcAmt', 'avgMcAmount'],
        treatAmount: ['treatCostAmt', 'avgTreatAmount'],
        deptProfit: ['totalAmount', 'deptProfitMargin']
      },
      // 新增：主页面选项卡控制
      activeMainTab: 'table',
      mainChartType: 'lineSort',
      mainChartField: 'outpatientVisits',
      mainChartInstance: null,
      // 在主图表字段定义中，确保字段key与接口返回字段一致
      mainChartFields: [
        { key: 'outpatientVisits', label: '门诊人次' },
        { key: 'prescriptionCount', label: '处方数量' },
        { key: 'totalAmount', label: '科室总金额(元)' },
        { key: 'avgAmount', label: '次均费用(元)' },
        { key: 'mcAmount', label: '药品金额(元)' },
        { key: 'avgMcAmount', label: '次均药费(元)' },
        { key: 'treatAmount', label: '诊疗金额(元)' },
        { key: 'avgTreatAmount', label: '次均诊疗费(元)' },
        { key: 'purcAmt', label: '药品成本(元)' },
        { key: 'eisaiPurcAmt', label: '卫材成本(元)' },
        { key: 'treatCostAmt', label: '项目成本(元)' },
        { key: 'feeAmount', label: '科室费用支出(元)' },
        { key: 'deptProfit', label: '科室毛利(元)' },
        { key: 'deptProfitMargin', label: '科室毛利率(%)' }
      ],
      // 用于动态排序折线图的数据
      dynamicSortData: [],
      sortTimer: null
    }
  },
  computed: {
    currentTableData() {
      const { pageNum, pageSize } = this.pagination
      const start = (pageNum - 1) * pageSize
      const end = start + pageSize
      return this.tableData.slice(start, end)
    }
  },
  mounted() {
    this.setTableHeight()
    window.addEventListener('resize', this.setTableHeight)
    this.loadUserInfo()
    this.fetchDeptOptions()
    // 不再设置初始数据，将在获取科室选项后自动加载数据
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setTableHeight)
    if (this.chartInstance) {
      this.chartInstance.dispose()
    }
    if (this.mainChartInstance) {
      this.mainChartInstance.dispose()
    }
    if (this.sortTimer) {
      clearInterval(this.sortTimer)
    }
  },
  watch: {
    detailDialogVisible(val) {
      if (val) {
        this.$nextTick(() => {
          if (this.detailActiveTab === 'chart') {
            this.initChart()
          }
        })
      } else {
        if (this.chartInstance) {
          this.chartInstance.dispose()
          this.chartInstance = null
        }
      }
    },
    
    detailActiveTab(val) {
      if (val === 'chart' && this.detailDialogVisible) {
        this.$nextTick(() => {
          this.initChart()
        })
      }
    },
    
    // 监听主页面选项卡切换
    activeMainTab(val) {
      if (val === 'chart') {
        this.$nextTick(() => {
          this.initMainChart()
        })
      } else {
        if (this.mainChartInstance) {
          this.mainChartInstance.dispose()
          this.mainChartInstance = null
        }
        if (this.sortTimer) {
          clearInterval(this.sortTimer)
        }
      }
    }
  },
  methods: {
    // 处理筛选
    handleFilter() {
      console.log('筛选条件：', this.filters)
      this.pagination.pageNum = 1
      this.fetchTableData()
    },
    
    // 导出数据
    handleExport() {
      console.log('导出数据')
      this.$message({
        message: '导出成功',
        type: 'success'
      })
    },
    
    // 格式化金额
    formatAmount(value) {
      if (value === null || value === undefined) return '0.00'
      return Number(value).toFixed(2)
    },
    
    // 格式化带货币符号的金额
    formatAmountWithSymbol(value) {
      if (value === null || value === undefined) return '￥0.00'
      const numValue = Number(value)
      if (isNaN(numValue)) return '￥0.00'
      
      const parts = numValue.toFixed(2).split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return `￥${parts.join('.')}`
    },
    
    // 格式化百分比
    formatPercentage(value) {
      if (value === null || value === undefined) return '0.00%'
      const numValue = Number(value)
      if (isNaN(numValue)) return '0.00%'
      return numValue.toFixed(2) + '%'
    },
    
    // 设置表格高度
    setTableHeight() {
      const windowHeight = window.innerHeight
      const headerHeight = 60
      const filterHeight = 120
      const descriptionHeight = 100
      const paginationHeight = 40
      const margin = 40
      
      this.tableHeight = windowHeight - headerHeight - filterHeight - descriptionHeight - paginationHeight - margin
    },
    
    // 分页大小变化
    handleSizeChange(val) {
      this.pagination.pageSize = val
      this.pagination.pageNum = 1
    },
    
    // 当前页变化
    handleCurrentChange(val) {
      this.pagination.pageNum = val
    },
    
	// 格式化日期时间
	formatDateTime(date, format = 'yyyy-MM-dd HH:mm:ss') {
	  if (!date) return ''
	  const d = new Date(date)
	  const year = d.getFullYear()
	  const month = (d.getMonth() + 1).toString().padStart(2, '0')
	  const day = d.getDate().toString().padStart(2, '0')
	  const hours = d.getHours().toString().padStart(2, '0')
	  const minutes = d.getMinutes().toString().padStart(2, '0')
	  const seconds = d.getSeconds().toString().padStart(2, '0')
	  
	  if (format === 'yyyy-MM-dd HH:mm:ss') {
	    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
	  } else if (format === 'yyyy-MM-dd') {
	    return `${year}-${month}-${day}`
	  }
	  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
	},
	
    // 获取表格数据
    async fetchTableData() {
      this.loading = true
      try {
        // 准备请求参数
        const params = {}
        
        // 添加科室筛选
        if (this.filters.department && this.filters.department.length > 0) {
          params.deptIds = this.filters.department
        }
        
        // 添加时间筛选
		if (this.filters.dateRange && this.filters.dateRange.length === 2) {
		  params.startTime = this.filters.dateRange[0]
		  params.endTime = this.filters.dateRange[1]
		}
	
        console.log('请求参数:', params)
        
        // 调用接口获取数据
        const res = await otpatDept(params)
        
        if (res && res.data) {
          // 转换接口数据为表格数据格式
          this.tableData = this.transformApiData(res.data)
          this.pagination.total = this.tableData.length
          
          // 如果有图表实例，更新图表
          if (this.activeMainTab === 'chart' && this.mainChartInstance) {
            this.updateMainChart()
          }
          
          this.$message({
            message: '数据加载成功',
            type: 'success'
          })
        } else {
          this.tableData = []
          this.pagination.total = 0
          this.$message.warning('暂无数据')
        }
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('数据加载失败')
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    
    // 转换接口数据为表格数据格式
    transformApiData(apiData) {
      if (!apiData || !Array.isArray(apiData)) {
        return []
      }
      
      return apiData.map(item => {
        return {
          departmentName: item.deptName || '未知科室',
          deptImgUrl: '',
          outpatientVisits: item.opNum || 0,
          prescriptionCount: item.drxNum || 0,
          totalAmount: item.totalAmount || 0,
          avgAmount: item.avgAmount || 0,
          mcAmount: item.mcAmount || 0,
          avgMcAmount: item.avgMcAmount || 0,
          treatAmount: item.treatAmount || 0,
          avgTreatAmount: item.avgTreatAmount || 0,
          purcAmt: item.purcAmt || 0,
          eisaiPurcAmt: item.eisaiPurcAmt || 0,
          treatCostAmt: item.treatCostAmt || 0,  // 直接使用接口返回的字段
          feeAmount: item.feeAmount || 0,
          deptProfit: item.deptProfit || 0,  // 直接使用接口返回的字段
          deptProfitMargin: item.deptProfitMargin || 0  // 直接使用接口返回的字段
        }
      })
    },
    
    // 处理详情
    handleDetail(row) {
      this.detailData = { ...row }
      this.detailDialogVisible = true
      this.detailActiveTab = 'detail'
      this.activeField = 'outpatientVisits' // 默认选中门诊人次
      
      // 如果有图表实例，先销毁
      if (this.chartInstance) {
        this.chartInstance.dispose()
        this.chartInstance = null
      }
    },
    
    // 关闭详情
    handleCloseDetail() {
      this.detailDialogVisible = false
      if (this.chartInstance) {
        this.chartInstance.dispose()
        this.chartInstance = null
      }
    },
    
    // 字段点击
    handleFieldClick(fieldKey) {
      this.activeField = fieldKey
    },
    
    // 获取字段标签
    getFieldLabel(key) {
      const field = this.fields.find(f => f.key === key)
      return field ? field.label : ''
    },
    
    // 获取字段数量
    getFieldCount(key) {
      const value = this.detailData[key]
      if (value === null || value === undefined) return null
      return Number(value).toLocaleString()
    },
    
    // 获取字段描述
    getFieldDescription(key) {
      const field = this.fields.find(f => f.key === key)
      return field ? field.description : ''
    },
    
    // 获取字段公式
    getFieldFormula(key) {
      const field = this.fields.find(f => f.key === key)
      return field ? field.formula : null
    },
    
    // 获取数据时间范围
    getDataTimeRange() {
      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        const startDate = this.formatDate(this.filters.dateRange[0])
        const endDate = this.formatDate(this.filters.dateRange[1])
        return `${startDate} 至 ${endDate}`
      }
      return '未指定时间范围'
    },
    
    // 格式化日期
    formatDate(date, format = 'yyyy-MM-dd') {
      if (!date) return ''
      const d = new Date(date)
      const year = d.getFullYear()
      const month = (d.getMonth() + 1).toString().padStart(2, '0')
      const day = d.getDate().toString().padStart(2, '0')
      const hours = d.getHours().toString().padStart(2, '0')
      const minutes = d.getMinutes().toString().padStart(2, '0')
      const seconds = d.getSeconds().toString().padStart(2, '0')
      
      if (format === 'yyyy-MM-dd HH:mm:ss') {
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
      } else if (format === 'yyyy-MM-dd') {
        return `${year}-${month}-${day}`
      }
      return `${year}-${month}-${day}`
    },
    
	// 在handleReset方法中重置时间范围
	handleReset() {
	  // 重置筛选条件
	  this.filters = {
	    disposalName: '',
	    department: [],
	    dateRange: []
	  }
	  this.pageNum = 1
	  
	  // 设置默认时间范围为一周
	  const endTime = new Date();
	  const startTime = new Date();
	  startTime.setDate(endTime.getDate() - 7);
	  this.filters.dateRange = [
	    this.formatDate(startTime, 'yyyy-MM-dd'),
	    this.formatDate(endTime, 'yyyy-MM-dd')
	  ]
	  
	  this.fetchTableData()
	  this.$message.info('已重置筛选条件')
	},
	
    // 获取相关指标
    getRelatedIndicators(key) {
      const relatedKeys = this.relatedIndicators[key] || []
      return relatedKeys.map(relatedKey => {
        const field = this.fields.find(f => f.key === relatedKey)
        return field ? { key: field.key, label: field.label } : null
      }).filter(item => item !== null)
    },
    
    // 初始化图表
    initChart() {
      if (this.chartInstance) {
        this.chartInstance.dispose()
      }
      
      const chartDom = this.$refs.chartRef
      if (!chartDom) {
        this.$nextTick(() => {
          this.renderChart()
        })
        return
      }
      
      this.renderChart()
    },
    
    // 渲染图表
    renderChart() {
      const chartDom = this.$refs.chartRef
      if (!chartDom) return
      
      this.chartInstance = echarts.init(chartDom)
      this.updateChart()
    },
    
    // 更新图表
    updateChart() {
      if (!this.chartInstance) {
        this.initChart()
        return
      }
      
      const chartData = this.prepareChartData()
      const option = this.getChartOption(chartData)
      
      this.chartInstance.setOption(option, true)
      
      // 响应窗口大小变化
      window.addEventListener('resize', () => {
        this.chartInstance.resize()
      })
    },
    
    // 准备图表数据
    prepareChartData() {
      const { detailData } = this
      const data = []
      
      // 使用所有字段数据
      this.fields.forEach(field => {
        let value = detailData[field.key] || 0
        // 如果是金额类数据，转换为数值
        if (field.key.includes('Amount') || field.key.includes('Cost') || 
            field.key.includes('Expense') || field.key.includes('Profit') || 
            field.key.includes('average')) {
          if (typeof value === 'string') {
            value = parseFloat(value.replace(/[￥,]/g, '')) || 0
          }
        }
        data.push({
          name: field.label,
          value: value
        })
      })
      
      return data
    },
    
    // 获取图表配置
    getChartOption(data) {
      const chartType = this.chartType
      
      const baseOption = {
        backgroundColor: '#fff',
        tooltip: {
          trigger: 'item',
          formatter: '{b}: {c}'
        },
        title: {
          text: `${this.detailData.departmentName} - 统计指标分析`,
          left: 'center',
          top: 20,
          textStyle: {
            fontSize: 16,
            fontWeight: 'bold'
          }
        }
      }
      
      switch (chartType) {
        case 'polarBar':
          return {
            ...baseOption,
            polar: {
              radius: [30, '80%']
            },
            angleAxis: {
              type: 'category',
              data: data.map(item => item.name),
              startAngle: 75
            },
            radiusAxis: {
              type: 'value',
              min: 0
            },
            series: [{
              type: 'bar',
              data: data.map(item => item.value),
              coordinateSystem: 'polar',
              label: {
                show: true,
                position: 'middle',
                formatter: '{b}\n{c}',
                fontSize: 10
              },
              itemStyle: {
                color: function(params) {
                  const colors = [
                    '#1890ff', '#13c2c2', '#52c41a', '#faad14', 
                    '#f5222d', '#eb2f96', '#722ed1', '#2f54eb',
                    '#fa8c16', '#a0d911', '#d4380d', '#096dd9',
                    '#08979c', '#7cb305'
                  ]
                  return colors[params.dataIndex % colors.length]
                }
              }
            }],
            legend: {
              show: false
            }
          }
          
        case 'line':
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: data.map(item => item.name),
              axisLabel: {
                interval: 0,
                rotate: 45,
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: [{
              data: data.map(item => item.value),
              type: 'line',
              smooth: true,
              lineStyle: {
                color: '#1890ff',
                width: 3
              },
              itemStyle: {
                color: '#1890ff'
              },
              areaStyle: {
                color: 'rgba(24, 144, 255, 0.1)'
              },
              symbol: 'circle',
              symbolSize: 8
            }]
          }
          
        case 'pie':
          return {
            ...baseOption,
            tooltip: {
              ...baseOption.tooltip,
              formatter: '{b}: {c} ({d}%)'
            },
            legend: {
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: 50,
              bottom: 20
            },
            series: [{
              name: '指标分布',
              type: 'pie',
              radius: ['40%', '70%'],
              center: ['40%', '50%'],
              data: data,
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              },
              itemStyle: {
                borderRadius: 6,
                borderColor: '#fff',
                borderWidth: 2
              },
              label: {
                formatter: '{b}: {c}',
                fontSize: 10
              }
            }]
          }
          
        case 'bar':
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: data.map(item => item.name),
              axisLabel: {
                interval: 0,
                rotate: 45,
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: [{
              data: data.map(item => item.value),
              type: 'bar',
              label: {
                show: true,
                position: 'top',
                fontSize: 10
              },
              itemStyle: {
                color: function(params) {
                  const colors = [
                    '#1890ff', '#13c2c2', '#52c41a', '#faad14', 
                    '#f5222d', '#eb2f96', '#722ed1', '#2f54eb',
                    '#fa8c16', '#a0d911', '#d4380d', '#096dd9',
                    '#08979c', '#7cb305'
                  ]
                  return colors[params.dataIndex % colors.length]
                }
              }
            }]
          }
          
        case 'scatter':
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: data.map(item => item.name),
              axisLabel: {
                interval: 0,
                rotate: 45,
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: [{
              data: data.map(item => ({
                value: item.value,
                name: item.name
              })),
              type: 'scatter',
              symbolSize: function(value) {
                return Math.min(Math.max(value.value / 100, 20), 60)
              },
              itemStyle: {
                color: '#1890ff'
              },
              label: {
                show: true,
                position: 'top',
                formatter: '{c}',
                fontSize: 10
              }
            }]
          }
          
        default:
          return baseOption
      }
    },
    
    // 获取指标值
    getIndicatorValue(key) {
      const value = this.detailData[key]
      if (key === 'deptProfitMargin') {
        return this.formatPercentage(value)
      } else if (key.includes('Amount') || key.includes('Cost') || key.includes('Expense') || key.includes('Profit') || key.includes('average')) {
        return this.formatAmountWithSymbol(value)
      } else {
        return value ? value.toLocaleString() : '0'
      }
    },
    
    // 加载用户信息
    loadUserInfo() {
      try {
        const userInfoStr = localStorage.getItem('userInfo')
        if (userInfoStr) {
          this.userInfo = JSON.parse(userInfoStr)
        }
      } catch (error) {
        console.error('加载用户信息失败:', error)
      }
    },
    
    // 获取科室选项
    async fetchDeptOptions() {
      try {
        const clinicId = this.userInfo.clinicId
        if (clinicId) {
          const res = await findAttribute({
            clinicId
          })
          if (res && res.data) {
            this.deptOptions = res.data
            console.log('获取科室选项:', this.deptOptions)
            
            // 获取科室选项后，自动加载数据
            this.fetchTableData()
          }
        } else {
          this.deptOptions = [
            { label: '内科', value: 'internal' },
            { label: '外科', value: 'surgery' },
            { label: '儿科', value: 'pediatrics' },
            { label: '妇产科', value: 'obstetrics' },
            { label: '眼科', value: 'ophthalmology' },
            { label: '耳鼻喉科', value: 'ent' },
            { label: '口腔科', value: 'stomatology' },
            { label: '皮肤科', value: 'dermatology' }
          ]
          
          // 获取默认科室选项后，自动加载数据
          this.fetchTableData()
        }
      } catch (error) {
        console.error('获取科室选项失败:', error)
        this.deptOptions = [
          { label: '内科', value: 'internal' },
          { label: '外科', value: 'surgery' },
          { label: '儿科', value: 'pediatrics' },
          { label: '妇产科', value: 'obstetrics' },
          { label: '眼科', value: 'ophthalmology' },
          { label: '耳鼻喉科', value: 'ent' },
          { label: '口腔科', value: 'stomatology' },
          { label: '皮肤科', value: 'dermatology' }
        ]
        
        // 获取默认科室选项后，自动加载数据
        this.fetchTableData()
      }
    },
    
    // 初始化主页面图表
    initMainChart() {
      if (this.mainChartInstance) {
        this.mainChartInstance.dispose()
      }
      
      const chartDom = this.$refs.mainChartRef
      if (!chartDom) {
        this.$nextTick(() => {
          this.renderMainChart()
        })
        return
      }
      
      this.renderMainChart()
    },
    
    // 渲染主页面图表
    renderMainChart() {
      const chartDom = this.$refs.mainChartRef
      if (!chartDom) return
      
      this.mainChartInstance = echarts.init(chartDom)
      this.updateMainChart()
    },
    
    // 更新主页面图表
    updateMainChart() {
      if (!this.mainChartInstance) {
        this.initMainChart()
        return
      }
      
      const chartData = this.prepareMainChartData()
      const option = this.getMainChartOption(chartData)
      
      this.mainChartInstance.setOption(option, true)
      
      // 响应窗口大小变化
      window.addEventListener('resize', () => {
        this.mainChartInstance.resize()
      })
    },
    
    // 准备主页面图表数据
    prepareMainChartData() {
      const chartData = []
      const { mainChartField } = this
      
      // 使用当前表格数据
      this.tableData.forEach(item => {
        let value = item[mainChartField] || 0
        
        // 如果是金额类数据，转换为数值
        if (mainChartField.includes('Amount') || mainChartField.includes('Cost') || 
            mainChartField.includes('Expense') || mainChartField.includes('Profit') || 
            mainChartField.includes('average')) {
          if (typeof value === 'string') {
            value = parseFloat(value.replace(/[￥,]/g, '')) || 0
          }
        }
        
        chartData.push({
          name: item.departmentName,
          value: value
        })
      })
      
      return chartData
    },
    
    // 获取主页面图表配置
    getMainChartOption(data) {
      const chartType = this.mainChartType
      const fieldLabel = this.mainChartFields.find(f => f.key === this.mainChartField)?.label || this.mainChartField
      
      const baseOption = {
        backgroundColor: '#fff',
        tooltip: {
          trigger: 'item',
          formatter: function(params) {
            if (chartType === 'scatter') {
              return `${params.data.name}: ${params.data.value}`
            }
            return `${params.data.name}: ${params.data.value}`
          }
        },
        title: {
          text: `各科室${fieldLabel}对比分析`,
          left: 'center',
          top: 20,
          textStyle: {
            fontSize: 16,
            fontWeight: 'bold'
          }
        }
      }
      
      switch (chartType) {
        case 'polarBar':
          return {
            ...baseOption,
            polar: {
              radius: [30, '80%']
            },
            angleAxis: {
              type: 'category',
              data: data.map(item => item.name),
              startAngle: 75
            },
            radiusAxis: {
              type: 'value',
              min: 0
            },
            series: [{
              type: 'bar',
              data: data.map(item => item.value),
              coordinateSystem: 'polar',
              label: {
                show: true,
                position: 'middle',
                formatter: '{b}\n{c}',
                fontSize: 10
              },
              itemStyle: {
                color: function(params) {
                  const colors = [
                    '#1890ff', '#13c2c2', '#52c41a', '#faad14', 
                    '#f5222d', '#eb2f96', '#722ed1', '#2f54eb',
                    '#fa8c16', '#a0d911', '#d4380d', '#096dd9',
                    '#08979c', '#7cb305'
                  ]
                  return colors[params.dataIndex % colors.length]
                }
              }
            }],
            legend: {
              show: false
            }
          }
          
        case 'line':
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: data.map(item => item.name),
              axisLabel: {
                interval: 0,
                rotate: 45,
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: [{
              data: data.map(item => item.value),
              type: 'line',
              smooth: true,
              lineStyle: {
                color: '#1890ff',
                width: 3
              },
              itemStyle: {
                color: '#1890ff'
              },
              areaStyle: {
                color: 'rgba(24, 144, 255, 0.1)'
              },
              symbol: 'circle',
              symbolSize: 8
            }]
          }
          
        case 'lineSort':
          // 折线图（动态排序折线图）
          return this.getDynamicSortLineOption(data, fieldLabel)
          
        case 'pie':
          return {
            ...baseOption,
            tooltip: {
              ...baseOption.tooltip,
              formatter: '{b}: {c} ({d}%)'
            },
            legend: {
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: 50,
              bottom: 20
            },
            series: [{
              name: '指标分布',
              type: 'pie',
              radius: ['40%', '70%'],
              center: ['40%', '50%'],
              data: data,
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              },
              itemStyle: {
                borderRadius: 6,
                borderColor: '#fff',
                borderWidth: 2
              },
              label: {
                formatter: '{b}: {c}',
                fontSize: 10
              }
            }]
          }
          
        case 'pieScroll':
          return {
            ...baseOption,
            tooltip: {
              ...baseOption.tooltip,
              formatter: '{b}: {c} ({d}%)'
            },
            legend: {
              type: 'scroll',
              orient: 'vertical',
              right: 10,
              top: 50,
              bottom: 20,
              pageButtonItemGap: 5,
              pageButtonGap: 5,
              pageButtonPosition: 'end',
              pageFormatter: '{current}/{total}',
              pageIcons: {
                horizontal: ['M0,0L12,-10L12,10Z', 'M0,0L-12,-10L-12,10Z'],
                vertical: ['M0,0L20,0L10,-20Z', 'M0,0L20,0L10,20Z']
              },
              pageIconColor: '#2f4554',
              pageIconInactiveColor: '#aaa',
              pageIconSize: 15,
              pageTextStyle: {
                color: '#333'
              }
            },
            series: [{
              name: '指标分布',
              type: 'pie',
              radius: ['40%', '70%'],
              center: ['40%', '50%'],
              data: data,
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowOffsetX: 0,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              },
              itemStyle: {
                borderRadius: 6,
                borderColor: '#fff',
                borderWidth: 2
              },
              label: {
                formatter: '{b}: {c}',
                fontSize: 10
              }
            }]
          }
          
        case 'bar':
          return {
            ...baseOption,
            xAxis: {
              type: 'category',
              data: data.map(item => item.name),
              axisLabel: {
                interval: 0,
                rotate: 45,
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: [{
              data: data.map(item => item.value),
              type: 'bar',
              label: {
                show: true,
                position: 'top',
                fontSize: 10
              },
              itemStyle: {
                color: function(params) {
                  const colors = [
                    '#1890ff', '#13c2c2', '#52c41a', '#faad14', 
                    '#f5222d', '#eb2f96', '#722ed1', '#2f54eb',
                    '#fa8c16', '#a0d911', '#d4380d', '#096dd9',
                    '#08979c', '#7cb305'
                  ]
                  return colors[params.dataIndex % colors.length]
                }
              }
            }]
          }
          
        case 'barNegative':
          // 交错正负轴标签柱状图
          const barNegativeData = data.map(item => {
            const value = item.value;
            // 创建正负交错的数据
            return {
              name: item.name,
              value: Math.random() > 0.5 ? value : -value
            };
          });
          
          return {
            ...baseOption,
            title: {
              ...baseOption.title,
              text: `各科室${fieldLabel}交错正负轴柱状图`
            },
            xAxis: {
              type: 'category',
              data: barNegativeData.map(item => item.name),
              axisLabel: {
                interval: 0,
                rotate: 45,
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '{value}'
              }
            },
            series: [{
              data: barNegativeData,
              type: 'bar',
              label: {
                show: true,
                position: 'top',
                fontSize: 10,
                formatter: function(params) {
                  return params.value > 0 ? `+${params.value}` : params.value;
                }
              },
              itemStyle: {
                color: function(params) {
                  return params.value >= 0 ? '#52c41a' : '#f5222d';
                }
              }
            }]
          }
          
        case 'barBreak':
          // 断轴上的柱状图
          return {
            ...baseOption,
            title: {
              ...baseOption.title,
              text: `各科室${fieldLabel}断轴柱状图`
            },
            xAxis: {
              type: 'category',
              data: data.map(item => item.name),
              axisLabel: {
                interval: 0,
                rotate: 45,
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              axisLabel: {
                formatter: '{value}'
              },
              splitLine: {
                show: false
              }
            },
            series: [{
              data: data.map(item => item.value),
              type: 'bar',
              label: {
                show: true,
                position: 'top',
                fontSize: 10
              },
              markLine: {
                symbol: 'none',
                lineStyle: {
                  type: 'solid',
                  color: '#f5222d',
                  width: 2
                },
                data: [
                  {yAxis: 50000}
                ]
              },
              itemStyle: {
                color: function(params) {
                  const colors = [
                    '#1890ff', '#13c2c2', '#52c41a', '#faad14', 
                    '#f5222d', '#eb2f96', '#722ed1', '#2f54eb',
                    '#fa8c16', '#a0d911', '#d4380d', '#096dd9',
                    '#08979c', '#7cb305'
                  ]
                  return colors[params.dataIndex % colors.length]
                }
              }
            }]
          }
          
        case 'heatmap':
          // 相关矩阵（热力图，矩阵坐标系）
          const heatmapFields = this.mainChartFields.slice(0, 5);
          const heatmapData = this.tableData.map(dept => {
            return heatmapFields.map(field => dept[field.key] || 0);
          });
          
          // 计算相关系数矩阵
          const correlationMatrix = (() => {
            const matrix = [];
            for (let i = 0; i < heatmapFields.length; i++) {
              matrix[i] = [];
              for (let j = 0; j < heatmapFields.length; j++) {
                // 简化计算，实际应该计算相关系数
                const corr = Math.random() * 2 - 1; // 模拟-1到1的相关系数
                matrix[i][j] = corr;
              }
            }
            return matrix;
          })();
          
          return {
            ...baseOption,
            title: {
              ...baseOption.title,
              text: '科室指标相关矩阵（热力图）'
            },
            tooltip: {
              position: 'top',
              formatter: function(params) {
                return `X: ${heatmapFields[params.data[0]].label}<br/>
                        Y: ${heatmapFields[params.data[1]].label}<br/>
                        相关系数: ${params.data[2].toFixed(3)}`;
              }
            },
            grid: {
              top: '10%',
              right: '10%',
              bottom: '15%',
              left: '10%'
            },
            xAxis: {
              type: 'category',
              data: heatmapFields.map(f => f.label),
              splitArea: {
                show: true
              },
              axisLabel: {
                interval: 0,
                rotate: 45
              }
            },
            yAxis: {
              type: 'category',
              data: heatmapFields.map(f => f.label),
              splitArea: {
                show: true
              }
            },
            visualMap: {
              min: -1,
              max: 1,
              calculable: true,
              orient: 'horizontal',
              left: 'center',
              bottom: '0%',
              inRange: {
                color: ['#313695', '#4575b4', '#74add1', '#abd9e9', '#e0f3f8', '#ffffbf', '#fee090', '#fdae61', '#f46d43', '#d73027', '#a50026']
              }
            },
            series: [{
              name: '相关矩阵',
              type: 'heatmap',
              data: (() => {
                const result = [];
                for (let i = 0; i < heatmapFields.length; i++) {
                  for (let j = 0; j < heatmapFields.length; j++) {
                    result.push([i, j, correlationMatrix[i][j]]);
                  }
                }
                return result;
              })(),
              label: {
                show: true,
                formatter: function(params) {
                  return params.data[2].toFixed(2);
                }
              },
              emphasis: {
                itemStyle: {
                  shadowBlur: 10,
                  shadowColor: 'rgba(0, 0, 0, 0.5)'
                }
              }
            }]
          }
          
        case 'radar':
          // 雷达图（自定义雷达图）
          return this.getRadarOption(data, fieldLabel)
          
        case 'boxplot':
          // 盒须图（数据聚合）
          return this.getBoxplotOption(data, fieldLabel)
          
        case 'graph':
          // 关系图（关系图自动隐藏重叠标签）
          return this.getGraphOption(data, fieldLabel)
          
        case 'tree':
          // 树图（折线树图）
          return this.getTreeOption(data, fieldLabel)
          
        case 'treemap':
          // 矩形树图（矩形树图和旭日图的动画过渡）
          return this.getTreemapOption(data, fieldLabel)
          
        case 'sankey':
          // 桑基图（桑基图左对齐布局）
          return this.getSankeyOption(data, fieldLabel)
          
        case 'pictorialBar':
          // 象形柱图（象形主图变形为柱状图）
          return this.getPictorialBarOption(data, fieldLabel)
          
        default:
          return baseOption
      }
    },
    
    // 折线图（动态排序折线图）
    getDynamicSortLineOption(data, fieldLabel) {
      // 创建动态数据
      const categories = data.map(item => item.name);
      const values = data.map(item => item.value);
      
      // 初始化动态排序数据
      this.dynamicSortData = categories.map((category, index) => ({
        category,
        value: values[index],
        rank: index
      }));
      
      // 开始动态排序
      this.startDynamicSorting();
      
      return {
        ...this.getBaseOption(fieldLabel),
        xAxis: {
          type: 'category',
          data: categories
        },
        yAxis: {
          type: 'value',
          name: fieldLabel
        },
        series: [{
          data: values,
          type: 'line',
          smooth: true,
          lineStyle: {
            color: '#1890ff',
            width: 3
          },
          itemStyle: {
            color: '#1890ff'
          },
          areaStyle: {
            color: 'rgba(24, 144, 255, 0.1)'
          },
          symbol: 'circle',
          symbolSize: 8,
          label: {
            show: true,
            position: 'top',
            fontSize: 10
          }
        }],
        animation: true,
        animationDuration: 1000,
        animationEasing: 'cubicInOut'
      };
    },
    
    // 开始动态排序
        startDynamicSorting() {
          if (this.sortTimer) {
            clearInterval(this.sortTimer);
          }
          
          this.sortTimer = setInterval(() => {
            // 随机打乱数据顺序
            this.dynamicSortData.sort(() => Math.random() - 0.5);
            
            // 更新图表
            if (this.mainChartInstance && this.mainChartType === 'lineSort') {
              const sortedData = [...this.dynamicSortData].sort((a, b) => b.value - a.value);
              const categories = sortedData.map(item => item.category);
              const values = sortedData.map(item => item.value);
              
              this.mainChartInstance.setOption({
                xAxis: {
                  data: categories
                },
                series: [{
                  data: values
                }]
              });
            }
          }, 3000); // 每3秒排序一次
        },
        
        // 雷达图（自定义雷达图）
        getRadarOption(data, fieldLabel) {
          const indicator = data.map(item => ({
            name: item.name,
            max: Math.max(...data.map(d => d.value)) * 1.2
          }));
          
          return {
            ...this.getBaseOption(fieldLabel),
            radar: {
              indicator: indicator,
              shape: 'circle',
              splitNumber: 5,
              axisName: {
                color: '#666',
                fontSize: 10
              },
              splitLine: {
                lineStyle: {
                  color: ['#e6e6e6', '#e6e6e6', '#e6e6e6', '#e6e6e6', '#e6e6e6']
                }
              },
              splitArea: {
                show: true,
                areaStyle: {
                  color: ['rgba(250,250,250,0.3)', 'rgba(200,200,200,0.3)']
                }
              }
            },
            series: [{
              type: 'radar',
              data: [{
                value: data.map(item => item.value),
                name: fieldLabel,
                areaStyle: {
                  color: 'rgba(24, 144, 255, 0.3)'
                },
                lineStyle: {
                  color: '#1890ff',
                  width: 2
                },
                itemStyle: {
                  color: '#1890ff'
                },
                label: {
                  show: true,
                  formatter: '{c}'
                }
              }]
            }]
          };
        },
        
        // 盒须图（数据聚合）
        getBoxplotOption(data, fieldLabel) {
          const values = data.map(item => item.value);
          const sortedValues = [...values].sort((a, b) => a - b);
          
          const min = Math.min(...values);
          const q1 = this.calculateQ1(values);
          const median = this.calculateMedian(values);
          const q3 = this.calculateQ3(values);
          const max = Math.max(...values);
          
          return {
            ...this.getBaseOption(`${fieldLabel} - 盒须图`),
            xAxis: {
              type: 'category',
              data: ['数据分布']
            },
            yAxis: {
              type: 'value',
              name: fieldLabel
            },
            series: [{
              name: '盒须图',
              type: 'boxplot',
              data: [[
                min,
                q1,
                median,
                q3,
                max
              ]],
              itemStyle: {
                color: '#1890ff',
                borderColor: '#096dd9'
              },
              tooltip: {
                formatter: function(param) {
                  return [
                    '最大值: ' + param.data[4],
                    '上四分位数(Q3): ' + param.data[3],
                    '中位数: ' + param.data[2],
                    '下四分位数(Q1): ' + param.data[1],
                    '最小值: ' + param.data[0]
                  ].join('<br/>');
                }
              }
            }]
          };
        },
        
        // 关系图（关系图自动隐藏重叠标签）
        getGraphOption(data, fieldLabel) {
          const nodes = data.map((item, index) => ({
            name: item.name,
            value: item.value,
            symbolSize: Math.min(Math.max(item.value / 100, 20), 60),
            itemStyle: {
              color: this.getColorByIndex(index)
            }
          }));
          
          // 创建关系边
          const links = [];
          for (let i = 0; i < nodes.length; i++) {
            for (let j = i + 1; j < nodes.length; j++) {
              if (Math.random() > 0.7) { // 随机创建关系
                links.push({
                  source: nodes[i].name,
                  target: nodes[j].name,
                  value: Math.abs(nodes[i].value - nodes[j].value)
                });
              }
            }
          }
          
          return {
            ...this.getBaseOption(`${fieldLabel} - 关系图`),
            tooltip: {
              formatter: '{b}: {c}'
            },
            series: [{
              type: 'graph',
              layout: 'force',
              data: nodes,
              links: links,
              categories: [],
              roam: true,
              label: {
                show: true,
                position: 'right',
                formatter: '{b}',
                fontSize: 10
              },
              lineStyle: {
                color: 'source',
                curveness: 0.3
              },
              emphasis: {
                focus: 'adjacency',
                lineStyle: {
                  width: 4
                }
              },
              force: {
                repulsion: 100,
                edgeLength: 30,
                layoutAnimation: true
              }
            }]
          };
        },
        
        // 树图（折线树图）
        getTreeOption(data, fieldLabel) {
          // 创建树形结构数据
          const treeData = {
            name: '根节点',
            value: data.reduce((sum, item) => sum + item.value, 0),
            children: data.map(item => ({
              name: item.name,
              value: item.value,
              children: [
                {
                  name: '子节点1',
                  value: item.value * 0.6
                },
                {
                  name: '子节点2',
                  value: item.value * 0.4
                }
              ]
            }))
          };
          
          return {
            ...this.getBaseOption(`${fieldLabel} - 树图`),
            tooltip: {
              trigger: 'item',
              triggerOn: 'mousemove',
              formatter: '{b}: {c}'
            },
            series: [{
              type: 'tree',
              data: [treeData],
              top: '5%',
              left: '10%',
              bottom: '5%',
              right: '20%',
              symbolSize: 7,
              label: {
                position: 'left',
                verticalAlign: 'middle',
                align: 'right',
                fontSize: 10
              },
              leaves: {
                label: {
                  position: 'right',
                  verticalAlign: 'middle',
                  align: 'left',
                  fontSize: 10
                }
              },
              expandAndCollapse: true,
              animationDuration: 550,
              animationDurationUpdate: 750,
              lineStyle: {
                color: '#1890ff',
                width: 2,
                curveness: 0.3
              },
              itemStyle: {
                color: '#52c41a',
                borderColor: '#1890ff'
              }
            }]
          };
        },
        
        // 矩形树图（矩形树图和旭日图的动画过渡）
        getTreemapOption(data, fieldLabel) {
          const treemapData = data.map(item => ({
            name: item.name,
            value: item.value,
            children: [
              {
                name: '子项1',
                value: item.value * 0.6
              },
              {
                name: '子项2',
                value: item.value * 0.4
              }
            ]
          }));
          
          return {
            ...this.getBaseOption(`${fieldLabel} - 矩形树图`),
            tooltip: {
              formatter: function(info) {
                const value = info.value;
                const treePathInfo = info.treePathInfo;
                let treePath = '';
                for (let i = 1; i < treePathInfo.length; i++) {
                  treePath += treePathInfo[i].name;
                  if (i < treePathInfo.length - 1) {
                    treePath += ' > ';
                  }
                }
                return [
                  `${treePath}`,
                  `数值: ${value}`
                ].join('<br>');
              }
            },
            series: [{
              type: 'treemap',
              data: treemapData,
              width: '80%',
              height: '80%',
              top: '10%',
              left: '10%',
              roam: false,
              nodeClick: false,
              breadcrumb: {
                show: true
              },
              label: {
                show: true,
                formatter: '{b}',
                fontSize: 10
              },
              levels: [
                {
                  itemStyle: {
                    borderColor: '#666',
                    borderWidth: 1,
                    gapWidth: 1
                  }
                },
                {
                  colorSaturation: [0.3, 0.6],
                  itemStyle: {
                    borderColor: '#555',
                    borderWidth: 2,
                    gapWidth: 1
                  }
                }
              ],
              animation: true,
              animationDuration: 1000,
              animationEasing: 'cubicInOut'
            }]
          };
        },
        
        // 桑基图（桑基图左对齐布局）
        getSankeyOption(data, fieldLabel) {
          // 创建桑基图数据
          const sankeyData = [];
          const sankeyLinks = [];
          
          data.forEach((item, index) => {
            sankeyData.push({
              name: item.name,
              value: item.value
            });
            
            // 创建流向
            if (index < data.length - 1) {
              sankeyLinks.push({
                source: item.name,
                target: data[index + 1].name,
                value: Math.min(item.value, data[index + 1].value) * 0.8
              });
            }
          });
          
          return {
            ...this.getBaseOption(`${fieldLabel} - 桑基图`),
            tooltip: {
              trigger: 'item',
              formatter: '{b}: {c}'
            },
            series: [{
              type: 'sankey',
              layout: 'none',
              data: sankeyData,
              links: sankeyLinks,
              left: '5%',
              right: '20%',
              top: '5%',
              bottom: '5%',
              nodeWidth: 20,
              nodeGap: 8,
              layoutIterations: 32,
              label: {
                show: true,
                position: 'right',
                fontSize: 10
              },
              lineStyle: {
                color: 'source',
                curveness: 0.5,
                opacity: 0.6
              },
              itemStyle: {
                borderColor: '#fff',
                borderWidth: 1
              },
              emphasis: {
                focus: 'adjacency',
                lineStyle: {
                  opacity: 0.9
                }
              }
            }]
          };
        },
        
        // 象形柱图（象形主图变形为柱状图）
        getPictorialBarOption(data, fieldLabel) {
          return {
            ...this.getBaseOption(`${fieldLabel} - 象形柱图`),
            xAxis: {
              type: 'category',
              data: data.map(item => item.name),
              axisLabel: {
                interval: 0,
                rotate: 45,
                fontSize: 10
              }
            },
            yAxis: {
              type: 'value',
              name: fieldLabel
            },
            series: [{
              type: 'pictorialBar',
              symbol: 'path://M0,10 L10,10 C5.5,10 5.5,5 5,0 C4.5,5 4.5,10 0,10 z',
              data: data.map(item => ({
                value: item.value,
                symbolSize: ['80%', '100%']
              })),
              label: {
                show: true,
                position: 'top',
                fontSize: 10
              },
              itemStyle: {
                color: function(params) {
                  const colors = [
                    '#1890ff', '#13c2c2', '#52c41a', '#faad14', 
                    '#f5222d', '#eb2f96', '#722ed1', '#2f54eb',
                    '#fa8c16', '#a0d911', '#d4380d', '#096dd9',
                    '#08979c', '#7cb305'
                  ];
                  return colors[params.dataIndex % colors.length];
                },
                opacity: 0.8
              },
              animationEasing: 'elasticOut',
              animationDelay: function(idx) {
                return idx * 100;
              }
            }]
          };
        },
        
        // 获取基础图表配置
        getBaseOption(title) {
          return {
            backgroundColor: '#fff',
            tooltip: {
              trigger: 'item',
              formatter: '{b}: {c}'
            },
            title: {
              text: title,
              left: 'center',
              top: 20,
              textStyle: {
                fontSize: 16,
                fontWeight: 'bold'
              }
            }
          };
        },
        
        // 根据索引获取颜色
        getColorByIndex(index) {
          const colors = [
            '#1890ff', '#13c2c2', '#52c41a', '#faad14', 
            '#f5222d', '#eb2f96', '#722ed1', '#2f54eb',
            '#fa8c16', '#a0d911', '#d4380d', '#096dd9',
            '#08979c', '#7cb305'
          ];
          return colors[index % colors.length];
        },
        
        // 计算最小值
        calculateMin(values) {
          return Math.min(...values)
        },
        
        // 计算第一四分位数
        calculateQ1(values) {
          const sorted = [...values].sort((a, b) => a - b)
          const index = Math.floor(sorted.length * 0.25)
          return sorted[index]
        },
        
        // 计算中位数
        calculateMedian(values) {
          const sorted = [...values].sort((a, b) => a - b)
          const mid = Math.floor(sorted.length / 2)
          if (sorted.length % 2 === 0) {
            return (sorted[mid - 1] + sorted[mid]) / 2
          }
          return sorted[mid]
        },
        
        // 计算第三四分位数
        calculateQ3(values) {
          const sorted = [...values].sort((a, b) => a - b)
          const index = Math.floor(sorted.length * 0.75)
          return sorted[index]
        },
        
        // 计算最大值
        calculateMax(values) {
          return Math.max(...values)
        }
      }
    }
    </script>
    
    <style lang="less" scoped>
    .department-statistics {
      height: calc(100vh - 120px);
      overflow-y: auto;
      padding: 16px;
      box-sizing: border-box;
      background-color: #f5f7fa;
      display: flex;
      flex-direction: column;
      
      .header-section {
        margin-bottom: 16px;
        
        .main-tabs {
          // 重置Element UI默认样式
          ::v-deep .el-tabs__header {
            margin: 0;
            border-bottom: 1px solid #e4e7ed;
          }
          
          ::v-deep .el-tabs__nav-wrap {
            &::after {
              display: none; // 隐藏默认的底部边框
            }
          }
          
          ::v-deep .el-tabs__item {
            font-size: 16px;
            font-weight: bold;
            color: #333;
            padding: 0 30px;
            height: 40px;
            line-height: 40px;
            position: relative;
            border: none;
            background: transparent;
            transition: all 0.3s;
            
            &:hover {
              color: #1890ff;
            }
            
            // 非激活选项卡样式
            &.is-active {
              color: #1890ff;
              background-color: #e6f7ff; // 浅蓝色背景
              border: 1px solid #bae7ff;
              border-bottom: none;
              border-radius: 4px 4px 0 0;
              
              // 激活选项卡的下方连接线
              &::after {
                content: '';
                position: absolute;
                bottom: -1px;
                left: 0;
                right: 0;
                height: 2px;
                background-color: #1890ff;
                z-index: 3;
              }
            }
          }
          
          // 自定义激活条
          ::v-deep .el-tabs__active-bar {
            display: none; // 隐藏默认激活条
          }
        }
      }
      
      .filter-section {
        margin-bottom: 16px;
        
        .filter-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
          
          .filter-left {
            display: flex;
            align-items: center;
            flex-wrap: wrap;
            gap: 16px;
            
            .filter-item {
              display: flex;
              align-items: center;
              
              .filter-label {
                font-size: 14px;
                color: #333;
                margin-right: 8px;
                white-space: nowrap;
              }
            }
          }
          
          .filter-right {
            display: flex;
            gap: 8px;
          }
        }
        
        .filter-desc {
          font-size: 12px;
          color: #999;
          font-style: italic;
        }
      }
      
      .main-content-section {
        flex: 1;
        display: flex;
        flex-direction: column;
        min-height: 0;
        
        // 表格区域
        .table-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
          height: 100%;
          width: 100%;
          
          .loading-container,
          .empty-container {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            padding: 50px 0;
          }
          
          .pagination-section {
            margin-top: 16px;
            display: flex;
            justify-content: flex-start;
            flex-shrink: 0;
          }
        }
        
        // 图表区域 - 修改为div标签
        .chart-section {
          flex: 1;
          display: flex;
          flex-direction: column;
          min-height: 0;
          height: 100%;
          width: 100%;
          border: 1px solid #e8e8e8;
          border-radius: 4px;
          background: #fff;
          padding: 16px;
          box-sizing: border-box;
          
          .chart-controls {
            margin-bottom: 16px;
            display: flex;
            align-items: center;
            flex-shrink: 0;
            padding: 8px 0;
          }
          
          .chart-loading,
          .chart-empty {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            border: 1px solid #e8e8e8;
            border-radius: 4px;
            background: #fff;
          }
          
          .chart-container {
            flex: 1;
            min-height: 0;
            height: 100%;
            border: 1px solid #e8e8e8;
            border-radius: 4px;
            padding: 10px;
            background: #fff;
            
            // 确保图表容器填充整个可用空间
            > div {
              width: 100%;
              height: 100%;
            }
          }
        }
      }
      
      .description-section {
        margin-top: 16px;
        flex-shrink: 0;
        
        .description-title {
          font-size: 16px;
          font-weight: bold;
          color: #333;
          margin-bottom: 12px;
        }
        
        .description-content {
          font-size: 13px;
          color: #666;
          line-height: 1.6;
          
          p {
            margin-bottom: 8px;
          }
        }
      }
    }
    
    // 详情模态框样式
    ::v-deep .detail-dialog {
      border-radius: 8px !important;
      overflow: hidden;
      
      .el-dialog__header {
        background: linear-gradient(135deg, #ebfaff 0%, #00557f 100%);
        color: #00557f;
        border-bottom: 1px solid #ebeef5;
        border-radius: 8px 8px 0 0;
        
        .el-dialog__title {
          color: white;
          font-weight: bold;
        }
        
        .el-dialog__headerbtn {
          .el-dialog__close {
            color: white;
            
            &:hover {
              color: #f0f0f0;
            }
          }
        }
      }
      
      .el-dialog__body {
        padding: 0;
        
        .detail-container {
          display: flex;
          height: 600px;
          overflow: hidden;
          
          // 左侧区域样式
          .detail-left {
            width: 250px;
            background: #f8f9fa;
            border-right: 1px solid #e8e8e8;
            display: flex;
            flex-direction: column;
            
            .dept-header {
              padding: 20px;
              
              .dept-avatar-section {
                display: flex;
                align-items: center;
                gap: 15px;
                
                .dept-avatar {
                  flex-shrink: 0;
                  background: linear-gradient(135deg, #ebfaff 0%, #00557f 100%);
                  color: white;
                  font-size: 20px;
                  font-weight: bold;
                }
                
                .dept-name-section {
                  .dept-name {
                    font-size: 18px;
                    font-weight: bold;
                    color: #333;
                  }
                  
                  .online-indicator {
                    margin-left: 8px;
                    
                    .el-icon-success {
                      font-size: 18px;
                    }
                  }
                }
              }
            }
            
            .custom-divider {
              margin: 0 20px;
              background-color: #e8e8e8;
            }
            
            .field-list-container {
              flex: 1;
              overflow: hidden;
              display: flex;
              flex-direction: column;
              
              .field-list-title {
                padding: 15px 20px;
                font-size: 16px;
                font-weight: bold;
                color: #333;
                border-bottom: 1px solid #e8e8e8;
                background: #f0f2f5;
              }
              
              .field-list-content {
                flex: 1;
                overflow-y: auto;
                padding: 10px 0;
                
                .field-item {
                  padding: 12px 20px;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  cursor: pointer;
                  transition: all 0.3s;
                  border-left: 3px solid transparent;
                  
                  &:hover {
                    background-color: #e6f7ff;
                    border-left-color: #1890ff;
                  }
                  
                  &.active {
                    background-color: #e6f7ff;
                    border-left-color: #1890ff;
                    
                    .field-label {
                      color: #1890ff;
                      font-weight: bold;
                    }
                    
                    .field-count {
                      background-color: #1890ff;
                      color: white;
                    }
                  }
                  
                  .field-label {
                    font-size: 14px;
                    color: #333;
                    transition: color 0.3s;
                  }
                  
                  .field-count {
                    font-size: 12px;
                    background-color: #f5f5f5;
                    color: #666;
                    padding: 2px 8px;
                    border-radius: 10px;
                    transition: all 0.3s;
                  }
                }
              }
            }
          }
          
          // 右侧区域样式
          .detail-right {
            flex: 1;
			padding: 12px;
            overflow: hidden;
            .right-tabs {
              height: 100%;
              
              ::v-deep .el-tabs__header {
                margin: 0;
                padding: 0 20px;
                background: white;
                border-bottom: 1px solid #e8e8e8;
                
                .el-tabs__nav-wrap {
                  &::after {
                    height: 1px;
                  }
                }
                
                .el-tabs__item {
                  padding: 0 20px;
                  height: 50px;
                  line-height: 50px;
                }
              }
              
              ::v-deep .el-tabs__content {
                height: calc(100% - 50px);
                padding: 20px;
                overflow-y: auto;
                
                .el-tab-pane {
                  height: 100%;
                }
              }
              
              // 详细统计样式
              .detail-statistics-content {
                .statistics-title {
                  font-size: 18px;
                  font-weight: bold;
                  color: #333;
                  margin-bottom: 20px;
                  padding-bottom: 10px;
                  border-bottom: 2px solid #1890ff;
                }
                
                .descriptions-container {
                  margin-bottom: 30px;
                  
                  ::v-deep .el-descriptions__header {
                    margin-bottom: 20px;
                    
                    .el-descriptions__title {
                      font-size: 16px;
                      color: #333;
                    }
                  }
                  
                  ::v-deep .el-descriptions-item__label {
                    width: 100px;
                    text-align: right;
                    background-color: #fafafa;
                    font-weight: bold;
                  }
                  
                  ::v-deep .el-descriptions-item__content {
                    padding-left: 20px;
                  }
                }
                
                .related-indicators {
                  .related-title {
                    font-size: 16px;
                    font-weight: bold;
                    color: #333;
                    margin-bottom: 15px;
                    padding-bottom: 8px;
                    border-bottom: 1px solid #e8e8e8;
                  }
                  
                  .indicators-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
                    gap: 15px;
                    
                    .indicator-card {
                      cursor: pointer;
                      transition: all 0.3s;
                      
                      &:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                      }
                      
                      .card-header {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        
                        .card-title {
                          font-size: 12px;
                          color: #666;
                          margin-bottom: 5px;
                          text-align: center;
                        }
                        
                        .card-value {
                          font-size: 16px;
                          font-weight: bold;
                          color: #1890ff;
                          text-align: center;
                        }
                      }
                    }
                  }
                }
              }
              
              // 图表统计样式
              .chart-statistics-content {
                height: 100%;
                display: flex;
                flex-direction: column;
                
                .chart-controls {
                  margin-bottom: 20px;
                  
                  .chart-type-selector {
                    display: flex;
                    align-items: center;
                    
                    .selector-label {
                      font-size: 14px;
                      color: #666;
                      margin-right: 10px;
                      white-space: nowrap;
                    }
                  }
                }
                
                .chart-container {
                  flex: 1;
                  min-height: 0;
                  border: 1px solid #e8e8e8;
                  border-radius: 4px;
                  padding: 10px;
                  background: #fff;
                }
                
                .chart-description {
                  margin-top: 20px;
                  padding: 15px;
                  background: #f8f9fa;
                  border-radius: 4px;
                  
                  .description-title {
                    font-size: 14px;
                    font-weight: bold;
                    color: #333;
                    margin-bottom: 8px;
                  }
                  
                  .description-content {
                    font-size: 13px;
                    color: #666;
                    line-height: 1.5;
                  }
                }
              }
            }
          }
        }
        
        .dialog-footer {
          padding: 10px 20px;
          border-top: 1px solid #ebeef5;
          display: flex;
          justify-content: flex-end;
        }
      }
    }
    
    // 字段列表滚动条样式
    .field-list-content::-webkit-scrollbar {
      width: 6px;
    }
    
    .field-list-content::-webkit-scrollbar-thumb {
      background-color: #c1c1c1;
      border-radius: 3px;
    }
    
    .field-list-content::-webkit-scrollbar-track {
      background-color: #f1f1f1;
    }
    
    // 响应式调整
    @media (max-width: 1366px) {
      ::v-deep .detail-dialog {
        .detail-container {
          .detail-left {
            width: 220px;
            
            .dept-header {
              .dept-avatar-section {
                flex-direction: column;
                align-items: flex-start;
                gap: 10px;
                
                .dept-avatar {
                  align-self: center;
                }
              }
            }
          }
          
          .detail-right {
            ::v-deep .el-tabs__item {
              padding: 0 10px;
            }
            
            .detail-statistics-content {
              .indicators-grid {
                grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)) !important;
              }
            }
          }
        }
      }
      
      .department-statistics {
        .table-section {
          ::v-deep .el-table {
            .el-table__cell {
              padding: 4px 8px;
              font-size: 12px;
            }
          }
        }
        
        .chart-section {
          .chart-controls {
            flex-wrap: wrap;
            gap: 8px;
          }
        }
      }
    }
    
    // 滚动条样式
    .department-statistics::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    
    .department-statistics::-webkit-scrollbar-thumb {
      background-color: #c1c1c1;
      border-radius: 3px;
    }
    
    .department-statistics::-webkit-scrollbar-track {
      background-color: #f1f1f1;
    }
    </style>